import React, { useEffect, useMemo } from 'react';
import { Button, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery } from '@apollo/client';

import { GET_STORES } from '../../apollo/queries/store';
import { MOVE_STORE_MANAGER } from '../../apollo/mutations/user';
import { HierarchyUser, Store } from '../../codegen/graphql';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../notifications';
import { GET_USERS } from '../../apollo/queries/user';
import { GET_OPS_HIERARCHY } from '../../apollo/queries/opsHierarchy';
import { useAuth } from '../../context/AuthProvider';
import { isUserAdmin } from '../../utility/userRoles';
import { mapStoreToManagerOption } from '../../helpers/storeMapping';

type Props = {
    close: () => void;
    currentStoreId: string;
    storeManager: HierarchyUser | null;
};

function MoveStoreForm({ close, storeManager, currentStoreId }: Props) {
    const { authUser } = useAuth();
    const {
        data: storesData,
        loading: loadingStores,
        error: storesError,
    } = useQuery(GET_STORES);

    const isAdmin = isUserAdmin(authUser);

    const [
        moveStoreManager,
        { loading: updatingUser, error: updateUserError },
    ] = useMutation(MOVE_STORE_MANAGER, {
        refetchQueries: [GET_USERS, GET_OPS_HIERARCHY, GET_STORES],
        awaitRefetchQueries: true,
    });

    const initialValues = {
        storeId: '',
    };

    const { onSubmit, reset, ...form } = useForm({
        initialValues,
        validate: {
            storeId: value => (!value ? 'Store is required' : null),
        },
    });

    const stores = useMemo(() => {
        if (!storesData?.getStores) {
            return [];
        }

        return storesData.getStores
            .filter((store: Store) => currentStoreId !== store.id)
            .map((store: Store) => mapStoreToManagerOption(store, isAdmin));
    }, [storesData, currentStoreId, isAdmin]);

    const onFormSubmit = onSubmit(async values => {
        if (!storeManager?.id) {
            showErrorNotification('Store manager information is missing');
            return;
        }

        try {
            await moveStoreManager({
                variables: {
                    userId: storeManager.id,
                    storeId: values.storeId,
                },
            });
            showSuccessNotification('Store manager moved successfully');
            close();
            reset();
        } catch (error) {
            form.setFieldError('storeId', 'Error moving store manager');
        }
    });

    const confirmDisabled = useMemo(() => {
        const noStoreSelected = !form.values.storeId;
        const noStoreManager = !storeManager?.id;
        return (
            noStoreSelected ?? noStoreManager ?? updatingUser ?? loadingStores
        );
    }, [form.values, storeManager, updatingUser, loadingStores]);

    useEffect(() => {
        if (storesError) {
            showErrorNotification('Error loading stores');
        }
        if (updateUserError) {
            form.setFieldError('storeId', 'Error moving store manager');
        }
    }, [storesError, updateUserError]);

    return (
        <form onSubmit={onFormSubmit} className="p-4">
            <div className="flex flex-col gap-4">
                <Select
                    name="storeId"
                    searchable
                    data={stores}
                    label="Select store"
                    placeholder="Store"
                    comboboxProps={{ withinPortal: true, zIndex: 1000 }}
                    {...form.getInputProps('storeId')}
                />
            </div>

            <Button
                type="submit"
                size="md"
                disabled={confirmDisabled}
                loading={updatingUser}
                className="mt-4"
            >
                Confirm
            </Button>
            <Button
                size="md"
                variant="outline"
                onClick={close}
                className="mt-4"
            >
                Cancel
            </Button>
        </form>
    );
}

export default MoveStoreForm;
